import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { api } from "../../../api/api";
import { IBusinessPolicyEquipment } from "./businessPolicyEquipment.types";
import { ERROR_MESSAGES } from "../../../constants/enums";
import {
  buildBinderRequest,
  ILeadScheduleScope,
} from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-additional-tabs
// 30s client-side bound so lead-scoped fetch surfaces the error + retry path
// deterministically (Requirements 4.5, 4.9).
const LEAD_BINDER_REQUEST_TIMEOUT_MS = 30000;

// Guard preventing a duplicate lead-scoped save from being dispatched while a save for
// the same record is already in flight.
let leadEquipmentSaveInFlight = false;

export const ADD_BUSINESS_POLICY_EQUIPMENT = "ADD_BUSINESS_POLICY_EQUIPMENT";
export const CLEAR_BUSINES_POLICY_EQUIPMENT_STATE =
  "CLEAR_BUSINES_POLICY_EQUIPMENT_STATE";
export const CLEAR_BUSINES_POLICY_EQUIPMENT = "CLEAR_BUSINES_POLICY_EQUIPMENT";
export const BUSINESS_POLICY_EQUIPMENT_NOT_FOUND =
  "BUSINESS_POLICY_EQUIPMENT_NOT_FOUND";

export const clearBusinessEquipmentRiskState = () =>
  action(CLEAR_BUSINES_POLICY_EQUIPMENT_STATE);

export const clearBusinessEquipment = () =>
  action(CLEAR_BUSINES_POLICY_EQUIPMENT);

export const FETCH_BUSINESS_POLICY_EQUIPMENT_LIST_PROGRESS =
  "FETCH_BUSINESS_POLICY_EQUIPMENT_LIST_PROGRESS";
export const FETCH_BUSINESS_POLICY_EQUIPMENT_LIST_SUCCESS =
  "FETCH_BUSINESS_POLICY_EQUIPMENT_LIST_SUCCESS";
export const FETCH_BUSINESS_POLICY_EQUIPMENT_LIST_FAILED =
  "FETCH_BUSINESS_POLICY_EQUIPMENT_LIST_FAILED";

export const fetchBusinessPolicyEquipmentListProgress = () =>
  action(FETCH_BUSINESS_POLICY_EQUIPMENT_LIST_PROGRESS);
export const fetchBusinessPolicyEquipmentListSuccess = (
  data: IBusinessPolicyEquipment[]
) => action(FETCH_BUSINESS_POLICY_EQUIPMENT_LIST_SUCCESS, { data });
export const fetchBusinessPolicyEquipmentListFailed = () =>
  action(FETCH_BUSINESS_POLICY_EQUIPMENT_LIST_FAILED);

export const fetchBusinessPolicyEquipmentListAsync =
  (
    customerPolicyId: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyEquipmentListProgress());
      let finalUrl = `/policy/get-policy-business-equipment?customer_policy_id=${customerPolicyId}`;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IBusinessPolicyEquipment[] = res.data.data;
      dispatch(fetchBusinessPolicyEquipmentListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyEquipmentListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const addBusinesPolicyEquipmentnAsync =
  (
    data: IBusinessPolicyEquipment,
    onCallback: (isSuccess: boolean, equipment?: IBusinessPolicyEquipment) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(`/policy/edit-policy-business-equipment`, {
        ...data,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Equipment saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Feature: lead-binder-additional-tabs
// Lead-scoped Equipment save. Persists with `lead_id` and NO `customer_policy_id`
// (the scope keys come from `buildBinderRequest`; the customer-policy identifiers are
// stripped from the record). A local in-flight guard prevents a duplicate save for the
// same record while one is already running. On success it re-fetches the list by the
// same `lead_id` (Requirements 4.2, 4.3). On failure/timeout it surfaces an error; the
// caller retains the entered input (Requirement 4.6). Mirrors
// `addLeadBusinessRiskLocationAsync`.
export const addLeadBusinessEquipmentAsync =
  (
    data: IBusinessPolicyEquipment,
    scope: ILeadScheduleScope,
    onCallback: (isSuccess: boolean, equipment?: IBusinessPolicyEquipment) => void,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    if (leadEquipmentSaveInFlight) {
      return;
    }
    leadEquipmentSaveInFlight = true;
    try {
      dispatch(saveLoaderProgress());
      const request = buildBinderRequest(
        "equipment",
        { kind: "lead", leadId: scope.leadId },
        "save"
      );
      const {
        customer_id,
        customer_policy_id,
        ...rest
      } = data as IBusinessPolicyEquipment & {
        customer_id?: unknown;
        customer_policy_id?: unknown;
      };
      const res = await api.post(
        request.url,
        { ...rest, ...request.body },
        { timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Equipment saved successfully!",
          displayAs: "snackbar",
        })
      );
      // Re-fetch the list by the same lead_id after a successful save.
      dispatch(fetchLeadEquipmentListAsync(scope, status));
      onCallback(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Equipment could not be saved. Please try again.",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      leadEquipmentSaveInFlight = false;
      dispatch(saveLoaderCompleted());
    }
  };

// Feature: lead-binder-additional-tabs
// Lead-scoped Equipment list fetch. Keyed by `lead_id` (never `customer_policy_id`),
// routed through the centralized endpoint map. On failure/timeout it flips the list to
// the Failed load state (retry surfaced by the sub-tab) and shows an error message
// (Requirements 4.1, 4.5, 4.9). Mirrors `fetchLeadBusinessRiskListAsync`.
export const fetchLeadEquipmentListAsync =
  (
    scope: ILeadScheduleScope,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyEquipmentListProgress());
      const request = buildBinderRequest(
        "equipment",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      let finalUrl = request.url;
      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyEquipment[] = res.data.data;
      dispatch(fetchBusinessPolicyEquipmentListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyEquipmentListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Equipment binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_BUSINESS_POLICY_EQUIPMENT_PROGRESS =
  "FETCH_BUSINESS_POLICY_EQUIPMENT_PROGRESS";
export const FETCH_BUSINESS_POLICY_EQUIPMENT_SUCCESS =
  "FETCH_BUSINESS_POLICY_EQUIPMENT_SUCCESS";
export const FETCH_BUSINESS_POLICY_EQUIPMENT_FAILED =
  "FETCH_BUSINESS_POLICY_EQUIPMENT_FAILED";

export const fetchBusinessPolicyEquipmentProgress = () =>
  action(FETCH_BUSINESS_POLICY_EQUIPMENT_PROGRESS);
export const fetchBusinessPolicyEquipmentSuccess = (
  data: IBusinessPolicyEquipment
) => action(FETCH_BUSINESS_POLICY_EQUIPMENT_SUCCESS, { data });
export const fetchBusinessPolicyEquipmentFailed = (errorMessage: string) =>
  action(FETCH_BUSINESS_POLICY_EQUIPMENT_FAILED, { errorMessage });

export const fetchBusinessPolicyEquipmentAsync =
  (
    customerPolicyId: number,
    incrementNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyEquipmentProgress());
      const res = await api.get(
        `/policy/get-policy-business-equipment?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incrementNumber}`
      );
      const data: IBusinessPolicyEquipment[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyEquipmentSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyEquipmentFailed(
            "Oops! We couldn't find any records associated with your equipment number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchBusinessPolicyEquipmentFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Feature: lead-binder-additional-tabs
// Lead-scoped single Equipment fetch (by incremental number). Keyed by `lead_id`
// (never `customer_policy_id`) via the centralized endpoint map. On failure/timeout it
// flips the record to the Failed load state so the sub-tab can surface the error +
// retry. Mirrors `fetchLeadBusinessRiskAsync`.
export const fetchLeadEquipmentAsync =
  (
    scope: ILeadScheduleScope,
    incrementNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyEquipmentProgress());
      const request = buildBinderRequest(
        "equipment",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      const finalUrl = `${request.url}&column=incremental_number&value=${incrementNumber}`;
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyEquipment[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyEquipmentSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyEquipmentFailed(
            "Oops! We couldn't find any records associated with your equipment number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(fetchBusinessPolicyEquipmentFailed(ERROR_MESSAGES.SERVER_ERROR));
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Equipment binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };
